import useAppModal from 'hooks/useAppModal';
import { LanguageCodeUnderscored } from 'i18n/internationalization.types';
import { useParams } from 'react-router-dom';
import { PropertyOwner } from 'models/Owner';
import OwnerModal, { ownerModalId } from './OwnerModal';
import useOwnerMutations, { OwnerPayload } from './useOwnerMutation';
import { OwnerFormSchemaType } from './OwnerModal.schema';

const useOwnerModal = () => {
  const { openModal, closeModal } = useAppModal();
  const { createOrUpdateOwner } = useOwnerMutations();
  const { uid: propertyUid } = useParams();

  const successCallback = () => {
    closeModal(ownerModalId);
  };

  const handleSubmit = (values: OwnerFormSchemaType) => {
    // If is linking existing owner, we need to pass only the email
    if (values.linkedOwnerUid) {
      const owner = {
        email: values.owner?.email,
        permissionTypes: values.owner?.permissionTypes ?? null,
      };

      const payload = {
        owner,
        propertyUid: propertyUid as string,
      } satisfies OwnerPayload;

      return createOrUpdateOwner({
        propertyUid: propertyUid as string,
        payload,
        successCallback,
      });
    }

    const owner = {
      uid: values.owner?.uid ?? undefined,
      agencyCommissionRate: values.owner.agencyCommissionRate,
      businessName: values.owner.businessName || null,
      ccEmails: values.owner.ccEmails || null,
      email: values.owner.email,
      firstName: values.owner.firstName || null,
      lastName: values.owner.lastName || null,
      phoneNumber: values.owner.phoneNumber_base || null,
      pictureUrl: values.owner.pictureUrl || null,
      phoneNumberAreaCode: Number(
        values.owner.phoneNumber_areaCode?.replace('+', ''),
      ),
      preferredLocale:
        values.owner.preferredLocale ?? LanguageCodeUnderscored.EN_US,
      permissionTypes: values.owner.permissionTypes ?? null,
    };

    const payload = {
      owner,
      propertyUid: propertyUid as string,
    } satisfies OwnerPayload;

    return createOrUpdateOwner({
      propertyUid: propertyUid as string,
      payload,
      successCallback,
    });
  };

  const openAddOwnerModal = () => {
    openModal({
      id: ownerModalId,
      customContent: <OwnerModal onSubmit={handleSubmit} />,
    });
  };

  const openEditOwnerModal = (owner: PropertyOwner) => {
    openModal({
      id: ownerModalId,
      customContent: <OwnerModal onSubmit={handleSubmit} owner={owner} />,
    });
  };

  return {
    openAddOwnerModal,
    openEditOwnerModal,
  };
};

export default useOwnerModal;
